import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { Cart } from 'types/cartServiceV2';
import { Address, AssigneeType, Group } from 'types/groupService';
import { EnrollmentPeriod } from 'types/enrollmentService';
import { Portfolio } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getCurrentDateInMDTFormat } from 'global/utils/dateHelper';
import { saveProposal as saveProposalToEnrollmentService } from 'api/operations/enrollmentService';
import { convertNewSaleCartIntoProductLine } from 'global/services-helpers/cartService';
import { saveProposal as saveProposalToCartService } from 'api/operations/cartServiceV2';
import { getPortfoliosById } from 'api/operations/productServiceV2';
import { FetchResult } from 'apollo-link';
import { generateProposal } from 'api/operations/proposalServiceV3';
import { Catalog } from 'types/productServiceV2';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';

interface SaveProposalFields extends DefaultFormFields {
  name: FormControl<string>;
}

export class SaveProposalModalStore {
  rootStore: RootStore;

  saveForm: FormController<SaveProposalFields>;
  loading = false;
  showSaveProposalModal = false;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setShowSaveProposalModal(showSaveProposalModal: boolean): void {
    this.showSaveProposalModal = showSaveProposalModal;
  }

  openSaveProposalModal(groupName: string): void {
    this.setShowSaveProposalModal(true);
    this.saveForm.populateFields({ name: `${groupName} ${getCurrentDateInMDTFormat()}` });
  }

  async confirmSaveProposalModal(
    cart: Cart,
    group: Group,
    cartName: string,
    address: Address,
    catalog: Catalog,
    initiateFormAnswers?: Record<string, string>,
  ): Promise<void> {
    const inputProductLines = convertNewSaleCartIntoProductLine(cart);
    this.setLoading(true);
    const inputGroup = buildInputGroupFromGroup(group);
    // Update address in input group
    inputGroup.contactInfo.primaryAddress = { ...inputGroup.contactInfo.primaryAddress, ...address };

    const requests = [
      saveProposalToCartService(cart.id, this.saveForm.fields.name.value),
      saveProposalToEnrollmentService(
        {
          groupId: group.id,
          cartName,
          cartId: cart.id,
          effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
          productLines: inputProductLines,
          initiateFormAnswers,
        },
        catalog.rateSegment,
      ),
      this.rootStore.groupStore.updateGroup(inputGroup),
      getPortfoliosById(cart.cartItems.map((item) => toJS(item)).slice(), undefined, group.id, cart.id, 'no-cache'),
    ];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const results = await Promise.all(requests);
    await runInAction(async () => {
      const updateCard = (results[0] as FetchResult<{ saveProposal: Cart }>)?.data?.saveProposal as Cart;
      const period = (results[1] as FetchResult<{ saveProposal: EnrollmentPeriod }>)?.data
        ?.saveProposal as EnrollmentPeriod;
      const portfoliosByBc = results[3] as Portfolio[];
      await generateProposal({
        domainId: this.rootStore.userStore.sponsorId,
        catalogId: catalog.id,
        cart: updateCard,
        portfolios: portfoliosByBc,
        period,
        // Old groups may have null agentOfRecord (AOR was not required when they were created).
        group: {
          ...group,
          agentOfRecord: group.agentOfRecord ?? { id: '', name: '', type: AssigneeType.AGENT },
        },
      });

      runInAction(() => {
        this.setShowSaveProposalModal(false);
        this.setLoading(false);
      });
    });
  }

  closeSaveProposalModal(): void {
    this.setShowSaveProposalModal(false);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.saveForm = new FormController({
      name: new FormControl<string>('', { optional: false }),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
